import axios from "axios"
import { defaultMessageForStatus } from "./apiErrors"
import { validateAndApplyBearerToken } from "./interceptors/request"
import { validateUnauthorized } from "./interceptors/response"
import { PERMISO_DENEGADO_EVENT } from "./permisos/puede"

// Fallback a PRODUCCION a proposito: un build sin VITE_BACKEND_URL debe
// degradar a prod, NUNCA a prerelease (codigo viejo + BD de prod — incidente
// recargas abortadas 2026-06-10). Para dev/staging, setear VITE_BACKEND_URL
// en .env (ej. https://apirelease.celink.mx/api).
const backendUrl =
  import.meta.env.VITE_BACKEND_URL || "https://api.celink.mx/api"

const odooInstance = axios.create({
  baseURL: backendUrl,
})

odooInstance.interceptors.request.use(
  function (config) {
    validateAndApplyBearerToken(config)
    return config
  },
  function (error) {
    return Promise.reject(error)
  }
)

/** Mensaje amigable cuando falla la conexión (red, backend caído, URL incorrecta) */
function getConnectionErrorMessage(error: unknown): string {
  const base = "No se pudo conectar al servidor."
  const hint =
    !backendUrl || backendUrl === ""
      ? " Configura VITE_BACKEND_URL en tu archivo .env (copia .env.template a .env)."
      : ` Comprueba que el backend esté en marcha en ${backendUrl}`
  const code = (error as { code?: string })?.code
  if (code === "ERR_NETWORK" || code === "ECONNABORTED" || !(error as { response?: unknown })?.response) {
    return base + hint
  }
  return base
}

/**
 * Detecta el anti-pattern del backend: HTTP 200 con cuerpo
 * `{ status: false, message, errors }`. Si lo detecta, rechaza la promesa
 * con el cuerpo intacto (y promueve el primer error de campo al `message`
 * top-level) para que el catch del consumidor lo procese igual que una
 * falla HTTP 4xx/5xx y muestre el detalle por campo en lugar del "Error
 * de validación" genérico.
 */
function isBackendFailureBody(body: unknown): boolean {
  if (!body || typeof body !== "object") return false
  const b = body as { status?: unknown }
  return b.status === false || b.status === "false"
}

function extractFirstFieldError(errors: unknown): string | null {
  if (!errors || typeof errors !== "object" || Array.isArray(errors)) return null
  const obj = errors as Record<string, unknown>
  const firstKey = Object.keys(obj)[0]
  if (!firstKey) return null
  const fieldVal = obj[firstKey]
  if (Array.isArray(fieldVal) && typeof fieldVal[0] === "string" && fieldVal[0].trim()) {
    return fieldVal[0]
  }
  if (typeof fieldVal === "string" && fieldVal.trim()) return fieldVal
  return null
}

odooInstance.interceptors.response.use(
  function (response) {
    const payload = response.data ?? response
    if (isBackendFailureBody(payload)) {
      const p = payload as { message?: string; errors?: unknown }
      const fieldMsg = extractFirstFieldError(p.errors)
      return Promise.reject({ ...p, message: fieldMsg ?? p.message })
    }
    return payload
  },
  function (error) {
    const isCanceled =
      axios.isCancel(error) ||
      error?.code === "ERR_CANCELED" ||
      error?.name === "CanceledError" ||
      error?.name === "AbortError"
    if (isCanceled) {
      return Promise.reject(error)
    }

    const requestUrl = error?.config?.url
    if (
      error.response?.status === 401 &&
      requestUrl?.includes("/page/estadoportabilidad")
    ) {
      return Promise.reject(error)
    }
    validateUnauthorized(error)
    // 403 del candado de permisos: quizá le acaban de cambiar el perfil. useUser relee menú y
    // permisos (con freno) para que el sidebar y las pantallas se pongan al día sin re-login.
    if (error.response?.status === 403 && error.response?.data?.message === "Permission denied") {
      window.dispatchEvent(new Event(PERMISO_DENEGADO_EVENT))
    }
    if (error.response) {
      return Promise.reject(normalizeHttpError(error.response))
    }
    return Promise.reject({
      message: getConnectionErrorMessage(error),
      connectionFailed: true,
      original: error,
    })
  }
)

/**
 * Normaliza el rechazo por error HTTP: SIEMPRE un objeto plano con `message`
 * legible y `httpStatus` numérico. Antes se rechazaba con `error.response.data`
 * tal cual: el código HTTP se perdía para el consumidor y, si Laravel devolvía
 * HTML (500) o un string, el catch recibía algo sin `.message` y degradaba al
 * "Error desconocido" genérico. Los consumidores que hoy leen `error.message`
 * o `error.errors` siguen funcionando igual (el body viaja intacto en el objeto).
 */
function normalizeHttpError(response: { status: number; data: unknown }) {
  const data = response.data
  const base: Record<string, unknown> =
    data && typeof data === "object" && !Array.isArray(data)
      ? { ...(data as Record<string, unknown>) }
      : {}
  if (typeof base.message !== "string" || !(base.message as string).trim()) {
    base.message =
      typeof data === "string" && data.trim() && data.length < 200
        ? data
        : defaultMessageForStatus(response.status)
  }
  // El candado de permisos contesta en inglés ("Permission denied" / "Route not found") y ese texto
  // llegaba tal cual a los avisos: el usuario leía un error técnico en vez de "te falta un permiso".
  const traducido = MENSAJES_DEL_CANDADO[`${response.status} ${String(base.message).trim()}`]
  if (traducido) {
    base.backendMessage = base.message
    base.message = traducido
  }
  base.httpStatus = response.status
  return base
}

const MENSAJES_DEL_CANDADO: Record<string, string> = {
  "403 Permission denied": "Tu perfil no tiene permiso para esto. Pídeselo a quien administra los perfiles.",
  "404 Route not found": "Esta función todavía no está dada de alta en los permisos de tu marca. Avísale a soporte.",
}

export function setAxiosBaseURL(url: string) {
  odooInstance.defaults.baseURL = url
}

export { odooInstance }
